import { defaultKeymap } from "@codemirror/commands";
import { javascript } from "@codemirror/lang-javascript";
import { Compartment, type Line, Prec, type SelectionRange } from "@codemirror/state";
import {
	type EditorView as CodeMirrorView,
	type Command,
	type KeyBinding,
	type ViewUpdate,
	keymap as cmKeymap,
	drawSelection,
} from "@codemirror/view";
import {
	type NodeViewComponentProps,
	useEditorEffect,
	useEditorEventCallback,
	useSelectNode,
} from "@handlewithcare/react-prosemirror";
import { xcodeDark, xcodeLight } from "@uiw/codemirror-theme-xcode";
import ReactCodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import cx from "classnames";
import dynamic from "next/dynamic";
import { exitCode } from "prosemirror-commands";
import { redo, undo } from "prosemirror-history";
import { Selection, TextSelection } from "prosemirror-state";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { schema } from "@moment/api-collab/prosemirror-schema";
import { DropdownMenu } from "@moment/design-system/DropdownMenu";
import { useTheme } from "@moment/design-system/ThemeProvider";

import { isDesktopAppMode } from "~/auth/interop/common";
import { Button } from "~/ui/Button";

import {
	type Language,
	detectLanguage,
	languages,
} from "../../plugins/codemirror-plugin/languages";

const TerminalDisplay = dynamic(() => import("~/components/canvas/terminal/TerminalDisplay"), {
	ssr: false,
});

export const CodeBlock = forwardRef<HTMLDivElement | null, NodeViewComponentProps>(
	function CodeBlock({ nodeProps, ...props }, outerRef) {
		const ref = useRef<HTMLDivElement | null>(null);

		// IMPORTANT: When we don't default this to `true`, styles don't always get applied
		// correctly. This does suck but we don't have time to figure out how to fix it the right
		// way.
		const [editable, setEditable] = useState(true);
		useEditorEffect((view) => setEditable(view.editable), []);

		const [isSelected, setIsSelected] = useState(false);
		const [hasCmSelection, setHasCmSelection] = useState(false);
		const { node, getPos } = nodeProps;

		useSelectNode(
			() => {
				setIsSelected(true);
				cmViewRef.current?.focus();
			},
			() => {
				setIsSelected(false);
			}
		);

		const isSelectionInCodeBlock = useEditorEventCallback(
			(view) =>
				view.state.selection.from >= getPos() &&
				view.state.selection.to <= getPos() + node.nodeSize
		);

		const cmViewRef = useRef<CodeMirrorView | null>(null);

		const languageCompartmentRef = useRef<Compartment | null>();
		if (!languageCompartmentRef.current) {
			languageCompartmentRef.current = new Compartment();
		}
		const languageCompartment = languageCompartmentRef.current;

		const { resolvedTheme } = useTheme();

		const language = node.attrs["language"] ?? "Auto";

		const initialLanguage = useMemo(() => {
			const detected = detectLanguage(language, node.textContent);
			if (detected) {
				return detected[1];
			}
			return javascript();
		}, [language, node.textContent]);

		const [shellScriptOutput] = useState<string | undefined>();
		const isDesktopApp = isDesktopAppMode();

		// let _runButtonMessage = "";
		// if (language === "Shell") {
		// 	if (!isDesktopApp) {
		// 		_runButtonMessage = "Run shell script (not supported in web app)";
		// 	} else {
		// 		_runButtonMessage = "Run shell script";
		// 	}
		// } else {
		// 	if (!isDesktopApp) {
		// 		_runButtonMessage = "Change language to Shell and download desktop app to run";
		// 	} else {
		// 		_runButtonMessage = "Change language to 'Shell' to run";
		// 	}
		// }

		const onCommit = useEditorEventCallback((view) => {
			if (!exitCode(view.state, view.dispatch)) {
				return false;
			}
			view.focus();
			return true;
		});

		const onLanguageChange = useEditorEventCallback((view, _language: string) => {
			let language = _language as Language | "Auto";
			if (language !== "Auto" && !languages.find((l) => l === language)) {
				language = "Auto";
			}

			const tr = view.state.tr;
			view.dispatch(tr.setNodeMarkup(getPos(), undefined, { language }));
		});

		const onUndo = useEditorEventCallback((view) => {
			return undo(view.state, view.dispatch, view);
		});

		const onRedo = useEditorEventCallback((view) => {
			return redo(view.state, view.dispatch, view);
		});

		const onDelete = useEditorEventCallback((view, cmView: CodeMirrorView) => {
			if (cmView.state.doc.length === 0) {
				const pos = getPos();
				const emptyParagraph = schema.nodes.paragraph.create();

				let tr = view.state.tr;

				// IMPORTANT: Schema requires at least one block. If this is the very last block, we
				// need to insert a new one after to delete this one.
				if (view.state.doc.childCount === 1) {
					tr = tr.insert(
						Selection.near(view.state.doc.resolve(pos), 1).from,
						emptyParagraph
					);
				}

				tr = tr.replaceRangeWith(pos, pos + nodeProps.node.nodeSize + 1, emptyParagraph);
				tr = tr.setSelection(Selection.near(tr.doc.resolve(tr.mapping.map(pos)), 1));

				view.dispatch(tr.scrollIntoView());
				view.focus();
				return true;
			}

			return false;
		});

		const withMaybeEscape = useEditorEventCallback(
			(view, unit: "line" | "char", dir: -1 | 1, cmView: CodeMirrorView) => {
				const state = cmView.state;
				if (!state) {
					return false;
				}

				let main: SelectionRange | Line = state.selection.main;
				if (!main.empty) {
					return false;
				}

				if (unit == "line") {
					main = state.doc.lineAt(main.head);
				}

				if (dir < 0 ? main.from > 0 : main.to < state.doc.length) {
					return false;
				}

				const targetPos = (getPos() || 0) + (dir < 0 ? 0 : node.nodeSize);
				const sel = Selection.near(view.state.doc.resolve(targetPos), dir);

				let tr = view.state.tr;

				if (dir === -1) {
					tr = view.state.tr.setSelection(sel).scrollIntoView();
				} else if (dir === 1) {
					// Insert empty paragrpah if `code_block` is the last node in the document.
					if (
						sel.$anchor.node().type === schema.nodes.code_block &&
						!sel.$anchor.nodeAfter
					) {
						const emptyParagraph = schema.nodes.paragraph.create();
						tr = tr.insert(sel.$anchor.pos, emptyParagraph);
					}
					const newSel = Selection.near(tr.doc.resolve(sel.$anchor.pos + 1), dir);
					tr = tr.setSelection(newSel).scrollIntoView();
				}

				view.dispatch(tr);
				view.focus();

				return true;
			}
		);

		const keymap = useMemo<readonly KeyBinding[]>(
			() => [
				{ key: "ArrowUp", run: ((view) => withMaybeEscape("line", -1, view)) as Command },
				{ key: "ArrowLeft", run: ((view) => withMaybeEscape("char", -1, view)) as Command },
				{ key: "ArrowDown", run: ((view) => withMaybeEscape("line", 1, view)) as Command },
				{ key: "ArrowRight", run: ((view) => withMaybeEscape("char", 1, view)) as Command },
				{
					key: "Ctrl-Enter",
					run: onCommit as Command,
				},
				{ key: "Ctrl-z", mac: "Cmd-z", run: onUndo as Command },
				{
					key: "Shift-Ctrl-z",
					mac: "Shift-Cmd-z",
					run: onRedo as Command,
				},
				{ key: "Ctrl-y", mac: "Cmd-y", run: onRedo as Command },
				{ key: "Backspace", run: onDelete as Command },
				{ key: "Delete", run: onDelete as Command },
				{
					key: "Tab",
					run: (view) => {
						view.dispatch(view.state.replaceSelection("\t"));
						return true;
					},
				},
				{
					key: "Shift-Tab",
					run: () => {
						// Just ignore shift+tab until we know how to make this work.
						return true;
					},
				},
			],
			[onCommit, onDelete, onRedo, onUndo, withMaybeEscape]
		);

		const onUpdate = useEditorEventCallback((view, update: ViewUpdate) => {
			// const detected = detectLanguage(language, node.textContent);
			// if (detected && detected[0] !== language) {
			// 	setLanguage(detected[0]);
			// 	update.view.dispatch({
			// 		effects: languageCompartment.reconfigure(detected[1]),
			// 	});
			// }
			// if (this.updating || !update.view.hasFocus) {
			if (update.state.doc.toString() === node.textContent) {
				return;
			}
			if (!update.view.hasFocus) {
				return;
			}

			let offset = (getPos() ?? 0) + 1;
			const { main } = update.state.selection;
			const selFrom = offset + main.from;
			const selTo = offset + main.to;

			const tr = view.state.tr;

			const pmSel = tr.selection;
			if (update.docChanged || pmSel.from != selFrom || pmSel.to != selTo) {
				update.changes.iterChanges((fromA, toA, fromB, toB, text) => {
					if (text.length) {
						tr.replaceWith(offset + fromA, offset + toA, schema.text(text.toString()));
					} else {
						tr.delete(offset + fromA, offset + toA);
					}
					offset += toB - fromB - (toA - fromA);
				});

				tr.setSelection(TextSelection.create(tr.doc, selFrom, selTo));
			}

			view.dispatch(tr);
		});

		const extensions = useMemo(
			() => [
				resolvedTheme === "dark" ? xcodeDark : xcodeLight,
				Prec.highest(cmKeymap.of([...keymap, ...defaultKeymap])),
				drawSelection(),
				languageCompartment.of(initialLanguage),
			],
			[initialLanguage, keymap, languageCompartment, resolvedTheme]
		);

		useEffect(() => {
			function onBeforeInput(e: InputEvent) {
				e.stopPropagation();
			}

			const el = ref.current;

			el?.addEventListener("beforeinput", onBeforeInput);
			return () => {
				el?.removeEventListener("beforeinput", onBeforeInput);
			};
		}, [ref]);

		const onFocus = useEditorEventCallback((view) => {
			const pmSel = view.state.selection;
			const cmSel = cmViewRef.current?.state.selection.main;

			if (!cmSel) {
				return;
			}
			setHasCmSelection(true);

			const offset = (getPos() ?? 0) + 1;
			const selFrom = offset + cmSel.from;
			const selTo = offset + cmSel.to;

			if (pmSel.from === selFrom && pmSel.to === selTo) {
				return;
			}

			let tr = view.state.tr;
			tr = tr.setSelection(TextSelection.create(tr.doc, selFrom, selTo));
			view.dispatch(tr);
		});

		const cmElRef = useCallback(
			(cmRef: ReactCodeMirrorRef) => {
				const cmView = cmRef?.view ?? null;
				if (cmViewRef.current === cmView) {
					return;
				}
				cmViewRef.current = cmView;
				// When a new CodeBlock is created, if it contains
				// the ProseMirror selection, focus it
				if (cmViewRef.current && isSelectionInCodeBlock()) {
					cmViewRef.current.focus();
				}
			},
			[isSelectionInCodeBlock]
		);

		return (
			// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
			<div
				id={node.attrs["id"]}
				ref={(el) => {
					ref.current = el;
					if (!outerRef) {
						return;
					}
					if (typeof outerRef === "function") {
						outerRef(el);
					} else {
						outerRef.current = el;
					}
				}}
				className="mb-4"
				data-text-cell={node.attrs["id"]}
				contentEditable={false}
				onClick={(e) => {
					const ignoreClick = (e.target as Element).closest(".ignore-click");
					if (ignoreClick) {
						return;
					}
					cmViewRef.current?.focus();
				}}
			>
				<div {...props} className="ignore-click relative flex flex-col">
					<div className="cm-code-block relative transition-none">
						<ReactCodeMirror
							ref={cmElRef}
							className={cx(
								"[&>.cm-editor]:!bg-transparent",
								"[&>.cm-editor]:!scroll-mr-10",
								"[&>.cm-focused]:!outline-none",
								"[&>*]:transition-none",
								// A little extra padding so the language selector doesn't cut off
								// the text.
								"[&_.cm-editor_.cm-scroller]:!pr-[8rem]",
								// IMPORTANT: For some reason, in published/readonly mode, CodeMirror does not like to show the correct font
								"[&_.cm-editor_.cm-scroller]:font-mono",
								// IMPORTANT: On load, CodeMirror likes to show scrollbars until you click into it
								"[&_.cm-editor_.cm-scroller]:overflow-hidden",
								"font-normal",
								"[&_.cm-line]:leading-[1.4]",
								{
									"[&_.cm-editor_.cm-scroller]:focus-within:border-primary":
										editable,
								}
							)}
							onUpdate={onUpdate}
							value={node.textContent}
							basicSetup={{
								lineNumbers: false,
								foldGutter: false,
								highlightActiveLine: false,
								autocompletion: false,
							}}
							extensions={extensions}
							onFocus={onFocus}
							readOnly={!editable}
							// IMPORTANT: Race in the underlying library. If this is set to `true`
							// it will crash the whole page when you focus the editor.
							editable={editable ? undefined : false}
							onBlur={() => {
								setHasCmSelection(false);
							}}
						/>
						{(editable || (language && language !== "Auto")) && (
							<div
								className={cx(
									"ignore-click",
									"bg-secondary",
									"absolute",
									"right-px",
									"top-[17px]",
									"flex",
									"items-center",
									"space-x-1",
									"border-l",
									{
										"border-primary": isSelected || hasCmSelection,
									},
									"px-2",
									"pr-4"
								)}
							>
								<DropdownMenu.Root>
									<DropdownMenu.Trigger disabled={!editable} asChild>
										<Button preset="quaternary" size="xs">
											{language ? language : "Auto"}
										</Button>
									</DropdownMenu.Trigger>
									<DropdownMenu.Portal>
										<DropdownMenu.Content className="overflow-y-scroll max-h-64">
											<DropdownMenu.Item
												onSelect={() => onLanguageChange("Auto")}
											>
												Auto
											</DropdownMenu.Item>
											{languages.map((language) => (
												<DropdownMenu.Item
													key={language}
													onSelect={() => onLanguageChange(language)}
												>
													{language}
												</DropdownMenu.Item>
											))}
										</DropdownMenu.Content>
									</DropdownMenu.Portal>
								</DropdownMenu.Root>

								{/* Add this back once we have integrated shell support. */}
								{/* {language === "Shell" && (
									<BasicTooltip content={runButtonMessage}>
										<IconButton
											label={runButtonMessage}
											preset="secondary"
											borderRadius="default"
											elevation="flat"
											size="xs"
											disabled={!isDesktopApp || language !== "Shell"}
											onClick={async () => {
												const script = node.textContent;
												if (!script) {
													return;
												}

												try {
													const result =
														await window.desktopIpcApi?.executeScript(
															script
														);

													setShellScriptOutput(result);
												} catch (err: unknown) {
													const errString = isError(err)
														? err.toString()
														: String(err);
													setShellScriptOutput(errString);
												}
											}}
											icon={<PlayArrow />}
										/>
									</BasicTooltip>
								)} */}
							</div>
						)}
					</div>
					{isDesktopApp && language === "Shell" && shellScriptOutput && (
						<TerminalDisplay content={shellScriptOutput}></TerminalDisplay>
					)}
				</div>
			</div>
		);
	}
);
